// ── PROJECT PICKER ──
// Per MULTI-ROLE-DESIGN.md. Two presentations of the same content:
//   - <ProjectPickerPage /> (default export) — the standalone /projects
//     route. Used by LoggedInRedirect when a multi-project user hasn't
//     chosen a project yet.
//   - <ProjectPickerModal open onClose /> — popup variant opened from the
//     header pill's "View all projects" link. Replaces the older
//     SwitchStorytellerModal so there's one canonical UI for switching.
//
// Both share the same inner body component so behavior stays identical:
// click → switch + hard-reload to the right home; "Leave" → confirm + DB
// soft-delete; "Start a new memoir" → slot-check then onboarding or
// purchase flow.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext.jsx';
import { useAuth } from '@/context/AuthContext.jsx';
import { me, billing } from '@/services/api.js';

function roleLabel(role) {
  if (role === 'captain') return 'Story Captain';
  if (role === 'storyteller') return 'Storyteller';
  return 'Story Crew';
}

function homeForRole(role) {
  return role === 'storyteller' ? '/story' : '/storyboard';
}

// Shared inner content used by both the page and the modal.
function ProjectPickerBody({ onClose }) {
  const { projects, switchProject, refresh, loading } = useProject();
  const { profile, user } = useAuth();
  const navigate = useNavigate();
  const [confirmLeave, setConfirmLeave] = useState(null);
  const [leavingId, setLeavingId] = useState(null);
  const [leaveError, setLeaveError] = useState('');
  const [startingNew, setStartingNew] = useState(false);

  async function pick(p) {
    if (user && user.id) {
      try { localStorage.setItem('active_storyteller_' + user.id, p.storytellerProfileId); } catch {}
    }
    await switchProject(p.storytellerProfileId);
    // Hard reload so all mounted pages refetch the new storyteller's data.
    window.location.assign(homeForRole(p.role));
  }

  async function confirmLeaveNow() {
    if (!confirmLeave) return;
    setLeaveError('');
    setLeavingId(confirmLeave.storytellerProfileId);
    try {
      await me.leaveProject(confirmLeave.storytellerProfileId);
      await refresh();
      setConfirmLeave(null);
    } catch (err) {
      setLeaveError(err.message || 'Could not leave this project');
    } finally {
      setLeavingId(null);
    }
  }

  // Start-new flow with slot check — preserved from the old
  // SwitchStorytellerModal behavior. If the captain has an available slot,
  // go straight to onboarding; otherwise route them to the purchase flow.
  async function startNew() {
    if (startingNew) return;
    setStartingNew(true);
    try {
      const slots = await billing.storytellerSlots();
      if (slots && slots.available > 0) {
        if (onClose) onClose();
        navigate('/onboarding?add=1');
      } else {
        if (onClose) onClose();
        navigate('/buy-storyteller');
      }
    } catch (err) {
      console.warn('[ProjectPicker] slot check failed:', err.message);
      if (onClose) onClose();
      navigate('/onboarding?add=1');
    } finally {
      setStartingNew(false);
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid var(--tan)', borderTopColor: 'var(--brown)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      </div>
    );
  }

  if (projects.length === 0) {
    // Shouldn't normally render here — LoggedInRedirect handles
    // zero-project users by sending them to onboarding.
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center' }}>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '24px', marginBottom: '12px' }}>
          <em>You're not part of any memoir yet.</em>
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '24px' }}>
          Ask a family member to invite you, or start your own memoir.
        </p>
        <button onClick={startNew} disabled={startingNew} style={{
          background: 'var(--brown-dark)', color: 'var(--cream)', border: 'none',
          borderRadius: '100px', padding: '14px 28px', fontSize: '15px', fontWeight: 600,
          cursor: 'pointer', fontFamily: 'var(--font-sans)', opacity: startingNew ? 0.6 : 1,
        }}>
          {startingNew ? 'Starting…' : 'Start a new memoir'}
        </button>
      </div>
    );
  }

  const greeting = profile?.first_name ? `Hello, ${profile.first_name}.` : 'Welcome back.';

  return (
    <div>
      <p style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: '18px', color: 'var(--brown-ink)', marginBottom: '4px', textAlign: 'center' }}>
        {greeting}
      </p>
      <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: '24px', fontWeight: 400, marginBottom: '6px', textAlign: 'center' }}>
        Which story are you <em>working on</em>?
      </h1>
      <p style={{ fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center', marginBottom: '22px', fontFamily: 'var(--font-sans)' }}>
        You can switch projects anytime from the menu.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {projects.map(p => (
          <div
            key={p.storytellerProfileId}
            style={{
              background: 'var(--warm-white)', border: '1.5px solid var(--tan)',
              borderRadius: '14px', position: 'relative', fontFamily: 'var(--font-sans)',
            }}
          >
            <button
              onClick={() => pick(p)}
              style={{
                width: '100%', padding: '16px 18px', cursor: 'pointer',
                textAlign: 'left', display: 'flex', alignItems: 'center', gap: '14px',
                background: 'none', border: 'none', borderRadius: '14px',
                fontFamily: 'var(--font-sans)',
              }}
              onMouseEnter={e => { e.currentTarget.parentElement.style.borderColor = 'var(--brown)'; }}
              onMouseLeave={e => { e.currentTarget.parentElement.style.borderColor = 'var(--tan)'; }}
            >
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%',
                background: 'var(--brown)', color: 'var(--cream)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '15px', fontFamily: 'var(--font-serif)', fontStyle: 'italic',
                flexShrink: 0,
              }}>
                {(p.storytellerFirstName?.[0] || p.storytellerName?.[0] || '?').toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '15px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '2px' }}>
                  {p.storytellerName}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-hint)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    background: 'var(--cream)', padding: '2px 8px', borderRadius: '100px',
                    color: 'var(--brown-dark)', fontWeight: 500, fontSize: '10px',
                  }}>
                    {roleLabel(p.role)}
                  </span>
                  {p.bookStatus && p.bookStatus !== 'draft' && (
                    <span style={{ fontStyle: 'italic' }}>Book: {p.bookStatus}</span>
                  )}
                </div>
              </div>
              <span style={{ fontSize: '18px', color: 'var(--text-hint)' }}>→</span>
            </button>
            {p.role !== 'storyteller' && (
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmLeave(p); setLeaveError(''); }}
                style={{
                  position: 'absolute', top: '6px', right: '10px',
                  background: 'none', border: 'none', color: 'var(--text-hint)',
                  fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-sans)',
                  textDecoration: 'underline', padding: '4px 6px',
                }}
              >
                Leave
              </button>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        <button onClick={startNew} disabled={startingNew} style={{
          background: 'none', border: 'none', color: 'var(--brown)', fontSize: '13px',
          cursor: startingNew ? 'default' : 'pointer', fontFamily: 'var(--font-sans)',
          textDecoration: 'underline', opacity: startingNew ? 0.6 : 1,
        }}>
          {startingNew ? 'Starting…' : '+ Start a new memoir'}
        </button>
      </div>

      {confirmLeave && (
        <div onClick={() => leavingId ? null : setConfirmLeave(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(46,32,25,0.65)', zIndex: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--warm-white)', borderRadius: '20px', padding: '28px 24px',
            maxWidth: '400px', width: '100%', textAlign: 'center', fontFamily: 'var(--font-sans)',
          }}>
            <div style={{ fontSize: '38px', marginBottom: '10px' }}>👋</div>
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '20px', fontWeight: 400, marginBottom: '8px' }}>
              <em>Leave {confirmLeave.storytellerName}'s project?</em>
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '18px' }}>
              You'll lose access to this project's storyboard, chapters, and book preview.
              {confirmLeave.role === 'captain' && (
                <> If you're the only captain, you'll need to invite a co-captain first.</>
              )}
            </p>
            {leaveError && (
              <p style={{ fontSize: '12px', color: '#8B3A1C', background: '#FCE8E2', border: '1px solid #D07A5C', borderRadius: '8px', padding: '8px 12px', marginBottom: '14px', textAlign: 'left' }}>
                {leaveError}
              </p>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setConfirmLeave(null)}
                disabled={leavingId === confirmLeave.storytellerProfileId}
                style={{ flex: 1, background: 'var(--warm-white)', border: '1.5px solid var(--tan)', borderRadius: '100px', padding: '12px', fontSize: '14px', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
              >Cancel</button>
              <button
                onClick={confirmLeaveNow}
                disabled={leavingId === confirmLeave.storytellerProfileId}
                style={{ flex: 1, background: 'var(--red-soft)', color: '#fff', border: 'none', borderRadius: '100px', padding: '12px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
              >{leavingId === confirmLeave.storytellerProfileId ? 'Leaving…' : 'Yes, leave'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Standalone page route — full-screen background, no close button.
export default function ProjectPickerPage() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)', padding: '60px 24px' }}>
      <div style={{ maxWidth: '560px', margin: '0 auto' }}>
        <ProjectPickerBody />
      </div>
    </div>
  );
}

// Modal variant — opened from the header pill's "View all projects" link.
// Same content, wrapped in a modal shell with a close button.
export function ProjectPickerModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(46,32,25,0.55)', zIndex: 600,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--cream)', borderRadius: '20px',
        padding: '28px 24px 20px', width: '520px', maxWidth: '100%',
        maxHeight: '88vh', overflowY: 'auto', position: 'relative',
        boxShadow: '0 16px 60px rgba(46,32,25,0.35)',
      }}>
        <button onClick={onClose} aria-label="Close" style={{
          position: 'absolute', top: '12px', right: '14px',
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '22px', color: 'var(--text-hint)', lineHeight: 1, padding: '4px 8px',
          fontFamily: 'var(--font-sans)',
        }}>×</button>
        <ProjectPickerBody onClose={onClose} />
      </div>
    </div>
  );
}
